import {
    buildFieldSortCriterion,
    buildInteractiveResult,
    buildResultList,
    buildResultsPerPage,
    buildSearchBox,
    buildSort,
    SortOrder,
} from '@coveo/headless';
import * as changeCase from 'change-case';
import React from 'react';
import { createStore } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { useSearchParams } from '@/hooks/useSearchParams';

const queryState = {
    error: null,
    loading: false,
    data: null,
};

const createTermFinderStore = (props) => {
    const {
        headlessEngine,

        accordionItems,

        label,
        eyebrow,
        showResultsButtonLabel,
        hideResultsButtonLabel,

        noResultsComponent,
        errorComponent,

        initialTerm,
    } = props;

    const headlessSearchBox = buildSearchBox(headlessEngine, {
        options: { numberOfSuggestions: 10 },
    });
    const headlessResultList = buildResultList(headlessEngine);

    buildResultsPerPage(headlessEngine, { initialState: { numberOfResults: 99999 } });
    const criterion = buildFieldSortCriterion('title', SortOrder.Ascending);
    buildSort(headlessEngine, { initialState: { criterion } });

    const store = createStore()((set, get) => ({
        uiCopy: {
            label,
            eyebrow,
            showResultsButtonLabel,
            hideResultsButtonLabel,

            noResultsComponent,
            errorComponent,
        },
        state: {
            // possible states: 'accordion' | 'searchResults'
            mainContent: 'accordion',
        },
        accordionItems,
        initialTerm,
        term: '',
        lastSearchTerm: '',
        selectedSuggestionId: '',
        suggestions: { ...queryState },
        searchResults: { ...queryState },

        onTermChange: (term) => {
            set({ term, selectedSuggestion: '' });

            if (term.length >= 2) {
                headlessSearchBox.updateText(term);
            } else {
                headlessSearchBox.clear();
            }
        },

        onSelectedSuggestionChange: (term) => {
            const { selectedSuggestion } = get();

            // necessary to prevent clear field after blur
            if (selectedSuggestion && term === '') {
                return;
            }

            set({ term, selectedSuggestion: term });
            headlessSearchBox.selectSuggestion(term);
            get().onUpdateLastSearch(term);

            set((s) => ({ state: { ...s.state, mainContent: 'searchResults' } }));
        },

        onSearchTerm: async (term) => {
            set({ term, selectedSuggestion: '' });
            headlessSearchBox.submit();

            set((s) => ({ state: { ...s.state, mainContent: 'searchResults' } }));
        },

        onUpdateLastSearch: (term) => {
            set({ lastSearchTerm: term });
        },

        onShowAllResults: async () => {
            get().onTermChange('');
            get().onUpdateLastSearch('');
            headlessSearchBox.submit();

            set((s) => ({ state: { ...s.state, mainContent: 'searchResults' } }));
        },

        onShowAccordion: () => {
            get().onTermChange('');
            get().onUpdateLastSearch('');

            set((s) => ({
                state: { ...s.state, mainContent: 'accordion' },
                searchResults: { ...queryState },
                selectedSuggestionId: null,
            }));
        },

        subscribe: () => {
            const unsubscribeHeadlessSearchBox = headlessSearchBox.subscribe(() => {
                set((s) => ({
                    suggestions: {
                        ...s.suggestions,
                        error: headlessSearchBox.state.hasError,
                        loading: headlessSearchBox.state.isLoadingSuggestions,
                        data: headlessSearchBox.state.suggestions.map((s, i) => ({
                            id: i,
                            text: changeCase.capitalCase(s.rawValue),
                            rawValue: s.rawValue,
                        })),
                    },
                }));
            });

            const unsubscribeHeadlessResultList = headlessResultList.subscribe(() => {
                set((s) => ({
                    searchResults: {
                        ...s.searchResults,
                        error: headlessResultList.state.hasError,
                        loading: headlessResultList.state.isLoading,
                        data: headlessResultList.state.results.map((r, i) => {
                            const interactiveResultController = headlessEngine
                                ? buildInteractiveResult(headlessEngine, {
                                      options: { result: r },
                                  })
                                : null;
                            return {
                                id: r.uniqueId,
                                label: r.title,
                                href: r.clickUri,
                                target: '_self',
                                coveoAnalyticsCallbacks: interactiveResultController
                                    ? {
                                          cancelPendingSelect: () =>
                                              interactiveResultController.cancelPendingSelect(),
                                          onClick: () => interactiveResultController.select(),
                                          onContextMenu: () => interactiveResultController.select(),
                                          onMouseDown: () => interactiveResultController.select(),
                                          onMouseUp: () => interactiveResultController.select(),
                                          onTouchStart: () =>
                                              interactiveResultController.beginDelayedSelect(),
                                          onTouchEnd: () =>
                                              interactiveResultController.cancelPendingSelect(),
                                      }
                                    : undefined,
                            };
                        }),
                    },
                }));
            });

            return () => {
                unsubscribeHeadlessSearchBox();
                unsubscribeHeadlessResultList();
            };
        },
    }));

    return store;
};

/**
 * @see https://docs.pmnd.rs/zustand/guides/initialize-state-with-props
 */
export const TermFinderContext = React.createContext();

export const TermFinderProvider = ({ children, ...props }) => {
    const storeRef = React.useRef();
    const searchParams = useSearchParams();
    const initialTerm = searchParams.get('term') || '';

    // https://legacy.reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
    const getStore = () => {
        if (!storeRef.current) {
            storeRef.current = createTermFinderStore({ ...props, initialTerm });
        }

        return storeRef.current;
    };

    const store = getStore();

    React.useEffect(() => {
        const state = store.getState();

        // if there is an initial term on first mount, search for it
        if (state.initialTerm && state.state.mainContent !== 'searchResults') {
            state.onSearchTerm(state.initialTerm);
            state.onUpdateLastSearch(state.initialTerm);
        }

        const unsubscribe = store.getState().subscribe();

        return () => {
            unsubscribe();
        };
    }, [store]);

    return <TermFinderContext.Provider value={store}>{children}</TermFinderContext.Provider>;
};

export const useTermFinderStore = () => {
    const store = React.useContext(TermFinderContext);
    if (!store) throw new Error('Missing `TermFinderProvider` in the tree');

    return store;
};

export const useTermFinderContext = (selector, equalityFn) => {
    const store = React.useContext(TermFinderContext);
    if (!store) throw new Error('Missing `TermFinderProvider` in the tree');

    return useStoreWithEqualityFn(store, selector, equalityFn);
};
